import { useDispatch, useSelector } from 'react-redux';
import SiteFileUpload from './SiteFileUpload';
import { useEffect, useState } from 'react';
import { getApiStatus } from '../../../core/utils';
import { clearFileUploadStatus, uploadProfitAndLoss } from '../actions';
import { selectFileUploadStatus } from '../purchaseOrderReducer';
import { generateToastr } from '../../../core/toastMessages';
import { downloadProfitAndLossCSV } from '../helpers';
import moment from 'moment';
import { showDialog } from '../../../core/actions';

interface PLUploadFormProps {
  employeeId: number;
  statementPeriod: string;
  sites: any;
  formReset: any;
  intl: any;
}

interface FileObject {
  siteId: number;
  file: File;
}

const PAndLUploadForm = ({
  employeeId,
  statementPeriod,
  sites,
  formReset,
  intl,
}: PLUploadFormProps) => {
  const [fileArray, setFiles] = useState<FileObject[]>([]);
  const [siteWithFiles, setSiteWithFiles] = useState<any[]>([]);
  const fileTypes = ['csv'];

  // selectors
  const filesAddedStatus = useSelector((state) => getApiStatus(state, 'UPLOAD_PROFIT_AND_LOSS'));
  const fileUploadStatus = useSelector(selectFileUploadStatus);
  const dispatch = useDispatch();

  const fileAdded = (fileObject: FileObject) => {
    const newFileArray = [...fileArray];
    const updatedFile = newFileArray.find((f) => f.siteId === fileObject.siteId);
    if (updatedFile) {
      updatedFile.file = fileObject.file;
    } else {
      newFileArray.push(fileObject);
    }
    setFiles(newFileArray);
    setSiteWithFiles(newFileArray.map((f) => f.siteId));
  };

  const createSiteFileUploadListAndSort = () => {
    const tempSiteFileUploadList: any[] = [];
    const siteFileUploadList: any[] = sites.map((site: any) => {
      if (siteWithFiles.includes(site.id)) {
        tempSiteFileUploadList.push(
          <SiteFileUpload
            siteLabel={site.externalId ? `${site.externalId} - ${site.name}` : site.name}
            siteId={site.id}
            allowedFiletypes={fileTypes}
            fileAddedCallback={fileAdded}
            inputId={site.id}
            key={site.id}
            isLoading={filesAddedStatus}
            selectedFiles={fileArray.map((f) => f.siteId)}
            saved={true}
          />
        );
      } else {
        return (
          <SiteFileUpload
            siteLabel={site.externalId ? `${site.externalId} - ${site.name}` : site.name}
            siteId={site.id}
            allowedFiletypes={fileTypes}
            fileAddedCallback={fileAdded}
            inputId={site.id}
            key={site.id}
            isLoading={filesAddedStatus}
            selectedFiles={fileArray.map((f) => f.siteId)} // This is the prop that is being used in the SiteFileUpload component
            saved={false}
          />
        );
      }
    });

    return [...tempSiteFileUploadList, ...siteFileUploadList];
  };

  const handleSubmit = () => {
    const confirmProps = {
      files: fileArray,
      fileAddedCallback: fileAdded,
      onReady: () => {
        dispatch(
          uploadProfitAndLoss({
            statementPeriod: moment(statementPeriod).format('MM-YYYY'),
            employeeId: employeeId,
            files: fileArray,
          })
        );
      },
      text: `${intl.formatMessage({
        id: 'confirmProfitAndLossUpload',
      })}: ${moment(statementPeriod).format('MM-YYYY')}?`,
      title: intl.formatMessage({ id: 'previewUpload' }),
    };
    dispatch(showDialog('FILE_PREVIEW', confirmProps));
  };

  const handleFormUploadMessage = () => {
    if (fileUploadStatus === 'success') {
      generateToastr('success', intl.formatMessage({ id: 'fileUploadSuccess' }));
      formReset();
    } else if (fileUploadStatus === 'error') {
      generateToastr('error', intl.formatMessage({ id: 'fileUploadFailure' }));
    }

    dispatch(clearFileUploadStatus());
  };

  useEffect(() => {
    handleFormUploadMessage();
  }, [fileUploadStatus]);

  return (
    <>
      <div className="p-l-upload upload-form">{createSiteFileUploadListAndSort()}</div>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <button className="button" onClick={downloadProfitAndLossCSV}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <i className="uil uil-file-download"></i>
            {intl.formatMessage({ id: 'downloadTemplate' })}
          </div>
        </button>
        <button
          className="button confirm full-width"
          onClick={handleSubmit}
          disabled={!fileArray.length}
        >
          {intl.formatMessage({ id: 'submit' })}
        </button>
      </div>
    </>
  );
};

export default PAndLUploadForm;
